/**
 * OPUS ci · idea 7 — Dos opciones con precio.
 *
 * Una OPCIÓN es otra forma de construir lo que pide un renglón PRINCIPAL de la hoja de un plano («block 15»
 * en vez de «tabique rojo»): su concepto, la MISMA medición del renglón y los complementarios que ese
 * concepto pide, con su precio. Vive aparte de la hoja (`plano_opciones`, migr 180): no suma al total, no se
 * imprime con la hoja ni viaja a OPUS 27. Al «Usar esta opción» se cambia por lo que hay en la hoja, y lo que
 * había se guarda como opción `anterior` para poder volver.
 *
 * Aquí vive lo puro:
 *  - `ladoActualDeLaHoja`: lo que la hoja tiene HOY para un principal (el principal y sus hijos). De un
 *    complementario que comparten varios principales entra solo la parte de ESTE (`parteDelPrincipal`: las
 *    líneas de su generador que salen de los elementos del principal).
 *  - `compararOpciones`: las dos listas lado a lado, con lo que cambia, el total de cada una y la diferencia.
 *  - `mismaMedicion`: la medición del renglón actual vale tal cual para la opción si se cobra en la misma
 *    unidad.
 *
 * Nada de conceptos, separaciones ni precios fijos: todo sale de la hoja, del catálogo o de la IA.
 */
import type { PlanoRenglon, PlanoSeleccion } from './domain.js';
import { elementosDeSeleccion, esPorPiezas, type FotoGenerador, type LineaGenerador } from './numerosGeneradores.js';
import { unitsAreEquivalent } from './units.js';

export type EstadoOpcion = 'propuesta' | 'anterior';

/** Lo que le tocó a un renglón de la hoja al juntarse con la opción. */
export interface ParteJuntada {
  /** Las líneas de su generador que entraron con la opción. null = el renglón entero es de la opción. */
  lineas: LineaGenerador[] | null;
  /** Lo que se le sumó (exacto: así se le resta lo mismo al volver). */
  cantidad?: number;
  cantidadMedida?: number | null;
  /** De qué principales colgaba antes de juntarse (para dejarlo igual al volver). */
  vinculos?: { principalId: string | null; padres: string[] | null };
}

/** Por renglón de la hoja: lo que es de la opción. */
export type PartesDeLaOpcion = Record<string, ParteJuntada>;

/** Un renglón de una opción (o del lado actual de la comparación). */
export interface RenglonDeOpcion {
  rol: 'principal' | 'complemento';
  /** De dónde sale el concepto: un concepto del catálogo o uno que se genera con IA (sin precio todavía). */
  origen: 'catalogo' | 'ia';
  catalogId: string | null;
  clave: string | null;
  descripcion: string;
  unidad: string | null;
  cantidad: number;
  /**
   * El plano no da su cantidad (ni se pudo sacar de la medida del elemento): va en 0 a propósito, marcada, para
   * escribirla. Nunca un 0 callado: la comparación lo dice y no lo cuenta como si costara 0.
   */
  sinCantidad?: boolean;
  /** Lo que midió el plano si quien cotiza escribió otra cantidad (el generador es lo medido). null = sin corregir. */
  cantidadMedida?: number | null;
  precioUnitario: number | null;
  importe: number | null;
  /** Complementario: qué trabajo es («Castillos», «Aplanado»), como lo nombró la IA. */
  nombre?: string | null;
  /** Complementario: con qué regla sale del principal (la que dio la IA). */
  regla?: string | null;
  /** Números generadores: de dónde sale la cantidad. */
  generador: FotoGenerador | null;
  /** Propuesta para revisar (todo lo derivado de la opción entra así). */
  revisar: boolean;
  nota?: string | null;
  /** Partida en que entra (si no, la familia del concepto). */
  partida?: string | null;
  /** Lo agregó quien cotiza a mano a la opción (no salió del motor): nunca se junta con otro renglón por su cuenta. */
  aMano?: boolean;
  /** Lado actual / opción anterior: el renglón de la hoja de donde sale. */
  renglonId?: string | null;
  /**
   * Complementario que comparten varios principales: solo entra la parte de este (`cantidad`) y aquí va la
   * cantidad de todo el renglón compartido. null/ausente = el renglón es solo de este principal.
   */
  compartido?: { cantidadDelRenglon: number } | null;
}

/** Una opción guardada de un principal. */
export interface PlanoOpcion {
  id: string;
  planoId: string;
  principalId: string;
  nombre: string;
  estado: EstadoOpcion;
  /**
   * La parte del principal de la que es esta opción (sus elementos del plano). Cuando el principal junta
   * más elementos (un renglón por concepto), la opción solo cambia estos. null = todo el principal.
   */
  elementos: { tipo: 'capa' | 'bloque'; clave: string; vista?: string }[] | null;
  /**
   * Cuando la opción se JUNTÓ con otro renglón del mismo concepto: qué le tocó a cada renglón de la hoja
   * (las líneas de su generador que entraron con ella; null = el renglón entero). Con esto la parte se separa
   * exacta aunque el renglón ya juntara líneas de esos mismos elementos. null = no se juntó.
   */
  partes?: PartesDeLaOpcion | null;
  renglones: RenglonDeOpcion[];
  /**
   * Opción de un GRUPO de principales (p. ej. todos los muros): una opción por principal, juntas por `grupoId`.
   * Se usan y se deshacen juntas, en orden inverso. null = opción suelta.
   */
  grupoId?: string | null;
  grupoOrden?: number | null;
  grupoNombre?: string | null;
  grupoTotal?: number | null;
  /** Σ importes de lo que tiene precio. */
  total: number;
  /** Renglones sin precio (se generan con IA al usarla). */
  sinPrecio: number;
  createdAt: string;
  updatedAt: string;
}

const r6 = (n: number): number => Math.round(n * 1e6) / 1e6;
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** Identidad de un concepto para emparejar los dos lados: catálogo + clave, o la descripción del sintético; y la unidad. */
export function claveDeConceptoOpcion(r: Pick<RenglonDeOpcion, 'origen' | 'catalogId' | 'clave' | 'descripcion' | 'unidad'>): string {
  const concepto =
    r.origen === 'catalogo' && r.clave
      ? `cat:${r.catalogId ?? ''}|${r.clave}`
      : `ia:${r.descripcion.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase().replace(/\s+/g, ' ')}`;
  return `${concepto}|${(r.unidad ?? '').trim().toLowerCase().replace(/²/g, '2').replace(/³/g, '3')}`;
}

// ---------------------------------------------------------------------------------------------------
// La parte de un complementario compartido
// ---------------------------------------------------------------------------------------------------

/** ¿La línea del generador sale de este elemento? (misma capa/bloque; la vista solo si ambos la dicen). */
function lineaEsDelElemento(l: LineaGenerador, e: { tipo: string; clave: string; vista?: string; ids?: readonly string[] }): boolean {
  if (!l.elemento || l.elemento.tipo !== e.tipo || l.elemento.clave !== e.clave) return false;
  if (esPorPiezas(e) && l.pieza != null && !e.ids!.includes(l.pieza)) return false;
  return !l.elemento.vista || !e.vista || l.elemento.vista === e.vista;
}

/**
 * Lo que un complementario compartido saca de UN principal: las líneas de su generador que salen de los
 * elementos de ese principal y su suma. null = no se sabe (sin generador, o ninguna línea sale de él).
 */
export function parteDelPrincipal(
  foto: FotoGenerador | null | undefined,
  seleccionDelPrincipal: PlanoSeleccion | null | undefined,
): { parte: number; lineas: LineaGenerador[] } | null {
  if (!foto || foto.lineas.length === 0) return null;
  const els = elementosDeSeleccion(seleccionDelPrincipal);
  if (els.length === 0) return null;
  const lineas = foto.lineas.filter((l) => els.some((e) => lineaEsDelElemento(l, e)));
  if (lineas.length === 0) return null;
  return { parte: r6(lineas.reduce((s, l) => s + l.parcial, 0)), lineas };
}

/** ¿El renglón cuelga de este principal? Y si es solo suyo o lo comparte con otros. */
export function relacionConElPrincipal(r: Pick<PlanoRenglon, 'id' | 'rol' | 'principalId' | 'padres'>, principalId: string): 'propio' | 'compartido' | null {
  if (r.id === principalId || r.rol === 'principal') return null;
  const otros = r.padres ?? [];
  if (r.principalId === principalId) return otros.some((p) => p !== principalId) ? 'compartido' : 'propio';
  return otros.includes(principalId) ? 'compartido' : null;
}

function renglonDeLaHoja(r: PlanoRenglon): RenglonDeOpcion {
  return {
    rol: r.rol === 'principal' ? 'principal' : 'complemento',
    origen: r.catalogId && r.resourceClave ? 'catalogo' : 'ia',
    catalogId: r.catalogId,
    clave: r.resourceClave,
    descripcion: r.conceptoDescripcion,
    unidad: r.unidad,
    cantidad: r.cantidad,
    // En la hoja, un renglón en 0 es uno sin cantidad (la hoja lo marca «sin cantidad»).
    ...(!(r.cantidad > 0) ? { sinCantidad: true } : {}),
    cantidadMedida: r.cantidadMedida ?? null,
    precioUnitario: r.precioUnitario,
    importe: r.importe,
    regla: r.generador?.regla ?? null,
    generador: r.generador ?? null,
    revisar: r.revisar,
    partida: r.partida,
    renglonId: r.id,
  };
}

export interface LadoActual {
  principal: PlanoRenglon;
  /** El principal primero, luego sus hijos (los compartidos, con solo su parte). */
  renglones: RenglonDeOpcion[];
  /** Complementarios compartidos de los que no se sabe cuánto aporta este principal (sin generador). */
  sinParte: PlanoRenglon[];
  /**
   * true = la opción es de UNA PARTE del renglón: el principal junta más elementos que los de la opción
   * (un renglón por concepto: el concepto de la opción ya lo llevaba otro principal y se juntaron).
   */
  parcial: boolean;
}

type UnElementoSel = { tipo: 'capa' | 'bloque'; clave: string; vista?: string; ids?: string[] };

/**
 * ¿Los dos juegos de elementos son el mismo? (sin importar el orden). Una capa IFC con sus piezas se
 * identifica también por ellas: dos losas del mismo tipo son elementos distintos (AI-71).
 */
export function mismosElementos(a: readonly UnElementoSel[], b: readonly UnElementoSel[]): boolean {
  const k = (xs: readonly UnElementoSel[]) =>
    xs
      .map((e) => `${e.tipo}:${e.clave}@${e.vista ?? ''}${esPorPiezas(e) ? `#${[...new Set(e.ids)].sort().join(',')}` : ''}`)
      .sort()
      .join('|');
  return k(a) === k(b);
}

/** La parte de un renglón que sale de unos elementos (cantidad e importe en la misma proporción). */
function parteDelRenglon(
  r: PlanoRenglon,
  p: { parte: number; lineas: LineaGenerador[]; cantidad?: number; cantidadMedida?: number | null },
): RenglonDeOpcion {
  const fraccion = p.cantidad != null ? (r.cantidad > 0 ? p.cantidad / r.cantidad : 0) : p.parte / r.generador!.total;
  const cantidad = p.cantidad ?? r6(r.cantidad * fraccion);
  const importe =
    r.precioUnitario != null ? round2(r.precioUnitario * cantidad) : r.importe != null ? round2(r.importe * fraccion) : null;
  return {
    ...renglonDeLaHoja(r),
    cantidad,
    cantidadMedida: p.cantidadMedida !== undefined ? p.cantidadMedida : r.cantidadMedida != null ? p.parte : null,
    importe,
    generador: r.generador ? { ...r.generador, lineas: p.lineas, total: r6(p.parte) } : null,
    compartido: { cantidadDelRenglon: r.cantidad },
  };
}

/**
 * Lo que la hoja tiene hoy para un principal: él y sus hijos (por `principalId` y por `padres`). De un hijo
 * compartido entra solo su parte, sacada de las líneas de su generador (si quien cotiza corrigió la cantidad
 * a mano, en la misma proporción).
 *
 * `elementos` = la parte del principal de la que es la opción. Si el principal junta más elementos (un
 * renglón por concepto), entra solo lo que sale de ellos: del principal y de cada hijo, las líneas de su
 * generador que salen de esos elementos. null = el renglón no está, no es principal o no se puede separar.
 */
export function ladoActualDeLaHoja(
  renglones: readonly PlanoRenglon[],
  principalId: string,
  elementos?: readonly UnElementoSel[] | null,
  partes?: PartesDeLaOpcion | null,
): LadoActual | null {
  const principal = renglones.find((r) => r.id === principalId);
  if (!principal || principal.rol !== 'principal') return null;
  const todos = elementosDeSeleccion(principal.seleccion);
  // La opción es de una PARTE del renglón si sus elementos no son todos los del principal, o si se JUNTÓ con él
  // sin traer elementos (un concepto que el plano no dibuja): `partes` lo dice con las líneas que le tocaron.
  const juntoSinElementos = !!partes && principal.id in partes && partes[principal.id]!.lineas !== null && (!elementos || elementos.length === 0);
  const parcial = juntoSinElementos || (!!elementos && elementos.length > 0 && !mismosElementos(elementos, todos));
  const els = elementos ?? [];
  const sel: PlanoSeleccion | null = parcial && els.length > 0 ? { ...els[0]!, ...(els.length > 1 ? { ademas: els.slice(1) } : {}) } : parcial ? null : principal.seleccion;
  const out: RenglonDeOpcion[] = [];
  // Lo que se juntó con la opción, renglón por renglón (exacto): manda sobre la regla de los elementos.
  const exacta = (
    r: PlanoRenglon,
  ): { parte: number; lineas: LineaGenerador[]; cantidad?: number; cantidadMedida?: number | null } | 'todo' | null | undefined => {
    if (!partes || !(r.id in partes)) return undefined;
    const pj = partes[r.id]!;
    const ls = pj.lineas;
    if (ls == null) return 'todo';
    if (!r.generador) return ls.length === 0 && pj.cantidad != null ? { parte: 0, lineas: [], cantidad: pj.cantidad, cantidadMedida: pj.cantidadMedida ?? null } : null;
    const quedan = ls.map((l) => JSON.stringify(l));
    const lineas = r.generador.lineas.filter((l) => {
      const i = quedan.indexOf(JSON.stringify(l));
      if (i < 0) return false;
      quedan.splice(i, 1);
      return true;
    });
    // Sin líneas (lo de la opción se escribió a mano) solo vale si se sabe cuánto se le sumó.
    if (quedan.length > 0 || (lineas.length === 0 && pj.cantidad == null)) return null;
    return {
      parte: r6(lineas.reduce((t, l) => t + l.parcial, 0)),
      lineas,
      ...(pj.cantidad != null ? { cantidad: pj.cantidad } : {}),
      ...(pj.cantidadMedida !== undefined ? { cantidadMedida: pj.cantidadMedida } : {}),
    };
  };
  if (parcial) {
    const ex = exacta(principal);
    const p: { parte: number; lineas: LineaGenerador[]; cantidad?: number; cantidadMedida?: number | null } | null =
      ex && ex !== 'todo' ? ex : ex === undefined ? parteDelPrincipal(principal.generador, sel) : null;
    if (!p || (p.cantidad == null && (!principal.generador || !(principal.generador.total > 0)))) return null;
    out.push(parteDelRenglon(principal, p));
  } else out.push(renglonDeLaHoja(principal));
  const sinParte: PlanoRenglon[] = [];
  for (const r of renglones) {
    const rel = relacionConElPrincipal(r, principalId);
    if (!rel) continue;
    if (rel === 'propio' && !parcial) {
      out.push(renglonDeLaHoja(r));
      continue;
    }
    // Lo que se juntó con la opción en un renglón que ya estaba se separa exacto (si no, por elementos).
    if (partes && (parcial || r.id in partes)) {
      const ex = exacta(r);
      if (ex === undefined) continue;
      if (ex === 'todo') out.push(renglonDeLaHoja(r));
      else if (ex == null || (ex.cantidad == null && (!r.generador || !(r.generador.total > 0)))) sinParte.push(r);
      else if (rel === 'propio' && ex.lineas.length > 0 && ex.lineas.length === r.generador?.lineas.length) out.push(renglonDeLaHoja(r));
      else out.push(parteDelRenglon(r, ex));
      continue;
    }
    const p = parteDelPrincipal(r.generador, sel);
    if (!p || !r.generador || !(r.generador.total > 0)) {
      // Sin generador no se sabe su parte; con generador y sin líneas de estos elementos, es de los otros.
      if (!r.generador || !parcial) sinParte.push(r);
      continue;
    }
    // Todo el hijo sale de estos elementos y solo cuelga de este principal: es propio.
    if (rel === 'propio' && Math.abs(p.parte - r.generador.total) < 1e-9 && p.lineas.length === r.generador.lineas.length) {
      out.push(renglonDeLaHoja(r));
      continue;
    }
    out.push(parteDelRenglon(r, p));
  }
  return { principal, renglones: out, sinParte, parcial };
}

// ---------------------------------------------------------------------------------------------------
// La comparación
// ---------------------------------------------------------------------------------------------------

export function totalDeRenglones(rs: readonly RenglonDeOpcion[]): { total: number; sinPrecio: number } {
  let total = 0;
  let sinPrecio = 0;
  for (const r of rs) {
    if (r.importe != null && r.precioUnitario != null && r.precioUnitario > 0) total += r.importe;
    else if (!r.sinCantidad) sinPrecio++;
  }
  return { total: round2(total), sinPrecio };
}

export interface FilaComparada {
  actual: RenglonDeOpcion | null;
  opcion: RenglonDeOpcion | null;
  /** Lo que cambia entre los dos lados (otro concepto, otra cantidad, otro importe, o un lado no lo lleva). */
  cambia: boolean;
  /** Importe de la opción menos el actual (lo sin precio cuenta 0). */
  diferencia: number;
}

export interface Comparacion {
  filas: FilaComparada[];
  totalActual: number;
  totalOpcion: number;
  /** Opción − actual. Negativo = la opción cuesta menos. */
  diferencia: number;
  /** La diferencia en % del actual (null si el actual no tiene precio). */
  porcentaje: number | null;
  sinPrecioActual: number;
  sinPrecioOpcion: number;
  /** Renglones de la opción sin cantidad (el plano no la da: hay que escribirla). */
  sinCantidadOpcion: number;
}

const importeDe = (r: RenglonDeOpcion | null): number =>
  r && r.importe != null && r.precioUnitario != null && r.precioUnitario > 0 ? r.importe : 0;

/**
 * Las dos listas lado a lado: el principal con el principal y cada complementario con el del mismo concepto
 * del otro lado; lo que un lado no lleva queda solo. En el orden del lado actual y, al final, lo nuevo.
 */
export function compararOpciones(actual: readonly RenglonDeOpcion[], opcion: readonly RenglonDeOpcion[]): Comparacion {
  const filas: FilaComparada[] = [];
  const usados = new Set<number>();
  const principalB = opcion.findIndex((r) => r.rol === 'principal');
  for (const a of actual) {
    let j = -1;
    if (a.rol === 'principal' && principalB >= 0 && !usados.has(principalB)) j = principalB;
    else {
      const k = claveDeConceptoOpcion(a);
      j = opcion.findIndex((b, i) => !usados.has(i) && b.rol !== 'principal' && claveDeConceptoOpcion(b) === k);
    }
    const b = j >= 0 ? opcion[j]! : null;
    if (j >= 0) usados.add(j);
    filas.push(fila(a, b));
  }
  opcion.forEach((b, i) => {
    if (!usados.has(i)) filas.push(fila(null, b));
  });
  const ta = totalDeRenglones(actual);
  const tb = totalDeRenglones(opcion);
  const diferencia = round2(tb.total - ta.total);
  return {
    filas,
    totalActual: ta.total,
    totalOpcion: tb.total,
    diferencia,
    porcentaje: ta.total > 0 ? (diferencia / ta.total) * 100 : null,
    sinPrecioActual: ta.sinPrecio,
    sinPrecioOpcion: tb.sinPrecio,
    sinCantidadOpcion: opcion.filter((r) => r.sinCantidad).length,
  };
}

function fila(a: RenglonDeOpcion | null, b: RenglonDeOpcion | null): FilaComparada {
  const diferencia = round2(importeDe(b) - importeDe(a));
  const cambia =
    !a ||
    !b ||
    !!a.sinCantidad ||
    !!b.sinCantidad ||
    claveDeConceptoOpcion(a) !== claveDeConceptoOpcion(b) ||
    Math.abs(a.cantidad - b.cantidad) > 1e-6 ||
    Math.abs(diferencia) >= 0.005 ||
    (a.importe == null) !== (b.importe == null);
  return { actual: a, opcion: b, cambia, diferencia };
}

// ---------------------------------------------------------------------------------------------------
// La medición de la opción
// ---------------------------------------------------------------------------------------------------

/**
 * La medición del renglón actual para el concepto de la opción: si se cobra en la misma unidad, la MISMA
 * cantidad y el mismo generador (con la unidad del concepto nuevo). null = otra unidad: hay que volver a
 * medir el elemento con la magnitud del concepto nuevo.
 */
export function mismaMedicion(
  actual: Pick<RenglonDeOpcion, 'cantidad' | 'cantidadMedida' | 'unidad' | 'generador'>,
  unidadNueva: string | null | undefined,
): { cantidad: number; cantidadMedida: number | null; generador: FotoGenerador | null } | null {
  const ua = (actual.unidad ?? '').trim();
  const un = (unidadNueva ?? '').trim();
  const igual = ua === un || unitsAreEquivalent(ua, un);
  if (!igual) return null;
  return {
    cantidad: actual.cantidad,
    cantidadMedida: actual.cantidadMedida ?? null,
    generador: actual.generador ? { ...actual.generador, unidad: unidadNueva ?? actual.generador.unidad } : null,
  };
}

// ---------------------------------------------------------------------------------------------------
// Un GRUPO de opciones (varios principales a la vez)
// ---------------------------------------------------------------------------------------------------

/**
 * Un renglón por concepto: junta (suma cantidad e importe y une las líneas del generador) los renglones del
 * mismo concepto y unidad. Es como quedaría la hoja: «Así quedaría tu hoja». El principal y el complementario
 * del mismo concepto no se mezclan. Lo agregado a mano o sin líneas de generador no se junta.
 */
export function juntarPorConcepto(rs: readonly RenglonDeOpcion[]): RenglonDeOpcion[] {
  const out: RenglonDeOpcion[] = [];
  const idx = new Map<string, number>();
  for (const r of rs) {
    const junta = !r.aMano && !!r.generador && r.generador.lineas.length > 0;
    const k = `${r.rol}|${r.generador?.uso ?? ''}|${claveDeConceptoOpcion(r)}`;
    const i = junta ? idx.get(k) : undefined;
    if (i === undefined) {
      if (junta) idx.set(k, out.length);
      out.push(r);
      continue;
    }
    const a = out[i]!;
    const cantidad = r6(a.cantidad + r.cantidad);
    out[i] = {
      ...a,
      cantidad,
      sinCantidad: a.sinCantidad && r.sinCantidad ? true : undefined,
      cantidadMedida: a.cantidadMedida != null || r.cantidadMedida != null ? r6((a.cantidadMedida ?? a.cantidad) + (r.cantidadMedida ?? r.cantidad)) : null,
      importe: a.importe != null || r.importe != null ? round2((a.importe ?? 0) + (r.importe ?? 0)) : null,
      generador: { ...a.generador!, lineas: [...a.generador!.lineas, ...r.generador!.lineas], total: r6(a.generador!.total + r.generador!.total) },
      revisar: a.revisar || r.revisar,
    };
  }
  return out;
}

/** La comparación de un grupo: lo de hoy de todos los miembros contra la opción de todos, un renglón por concepto. */
export function compararGrupo(lados: readonly (readonly RenglonDeOpcion[])[], opciones: readonly (readonly RenglonDeOpcion[])[]): Comparacion {
  // Para emparejar, el principal de cada lado se compara por concepto (no hay UN principal): se marcan como complemento.
  const comoFilas = (xs: readonly (readonly RenglonDeOpcion[])[]) => juntarPorConcepto(xs.flat()).map((r) => ({ ...r, rol: 'complemento' as const }));
  return compararOpciones(comoFilas(lados), comoFilas(opciones));
}

/** Los renglones que de verdad necesitarán IA al usar el grupo: uno por concepto sin precio (no uno por miembro). */
export function sinPrecioDelGrupo(opciones: readonly (readonly RenglonDeOpcion[])[]): number {
  const unicos = new Set<string>();
  for (const r of juntarPorConcepto(opciones.flat())) {
    if (r.sinCantidad) continue;
    if (!(r.importe != null && r.precioUnitario != null && r.precioUnitario > 0)) unicos.add(`${r.rol}|${claveDeConceptoOpcion(r)}`);
  }
  return unicos.size;
}

/**
 * En qué orden se aplican los miembros de un grupo: las propuestas en el orden en que se armaron; las
 * `anterior` al revés (el último en usarse es el primero en deshacerse), así cada paso vuelve exacto.
 */
export function ordenDeAplicacion<T extends { estado: EstadoOpcion; grupoOrden?: number | null }>(miembros: readonly T[]): T[] {
  const asc = [...miembros].sort((a, b) => (a.grupoOrden ?? 0) - (b.grupoOrden ?? 0));
  return miembros.every((m) => m.estado === 'anterior') ? asc.reverse() : asc;
}
